import { createClient } from './supabase';
import { validatePassword } from './password-validation.service';
import type { NormalizedUser } from '../types/user-schema';
import type { UserRole } from '../types/roles';
import type { AuthSession } from '../types/auth';

/**
 * Improved Authentication Service
 * 
 * This service handles all authentication operations with Supabase,
 * with cleaner separation of concerns and better error handling.
 */
export class AuthServiceV2 {
  /**
   * Transforms the Supabase auth response to our application's AuthSession format
   */
  private transformAuthResponse(data: any, normalizedUser?: NormalizedUser | null): AuthSession {
    return {
      user: normalizedUser || data.user,
      access_token: data.session?.access_token || '',
      token_type: data.session?.token_type || 'bearer',
      refresh_token: data.session?.refresh_token || '',
      expires_in: data.session?.expires_in || 3600,
      metadata: data.session?.user?.user_metadata || {}
    };
  }

  /**
   * Fetch user profile data from the profiles table
   * This is a critical function that gets the user's role and business information
   */
  private async fetchUserProfile(userId: string) {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.warn('Profile fetch error:', error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error('Error during profile lookup:', err);
      return null;
    }
  }

  /**
   * Normalize user data with profile information
   */
  private async normalizeUserWithProfile(userData: any): Promise<NormalizedUser> {
    // Import dynamically to avoid circular dependencies
    const { refreshAndNormalizeUser } = await import('./normalize-user.service');
    return refreshAndNormalizeUser({ id: userData.id }, userData);
  }

  /**
   * Handle authentication errors with clear messages
   */
  private handleAuthError(error: any): never {
    console.error('Authentication error:', error);
    
    // Provide user-friendly error messages
    if (error.message?.includes('Invalid login credentials')) {
      throw new Error('Invalid email or password. Please try again.');
    }
    
    if (error.message?.includes('Email not confirmed')) {
      throw new Error('Please verify your email address before logging in.');
    }
    
    if (error.message?.includes('weak password')) {
      throw new Error('Password is too weak. It must be at least 8 characters with uppercase and special characters.');
    }
    
    // Default error
    throw new Error(error.message || 'Authentication failed. Please try again.');
  }

  /**
   * User login with email and password
   * No role checking - just basic authentication
   */
  async login(email: string, password: string): Promise<AuthSession> {
    try {
      console.log(`Attempting login for email: ${email}`);
      const supabase = await createClient();
      
      // Authenticate with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) throw error;
      if (!data || !data.user) throw new Error('No session data returned');
      
      // Set up session refresh
      this.setupSessionRefresh();
      
      // Get normalized user data
      const normalizedUser = await this.normalizeUserWithProfile(data.user);
      
      return this.transformAuthResponse(data, normalizedUser);
    } catch (error) {
      return this.handleAuthError(error);
    }
  }

  /**
   * Role-based login with improved error handling
   */
  async loginWithRole(email: string, password: string, role: UserRole = 'business'): Promise<AuthSession> {
    try {
      console.log(`Attempting ${role} login for email: ${email}`);
      const supabase = await createClient();
      
      // First authenticate the user with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) throw error;
      if (!data || !data.user) throw new Error('No session data returned');
      
      // Fetch user profile to verify role
      const profileData = await this.fetchUserProfile(data.user.id);
      
      // Log role information but don't block login on mismatch
      if (profileData?.role && profileData.role !== role) {
        console.warn(`Role mismatch: Expected ${role}, found ${profileData.role}. Continuing with login.`);
      }
      
      // Set up session refresh
      this.setupSessionRefresh();
      
      // Get normalized user data with profile information
      const normalizedUser = await this.normalizeUserWithProfile({
        ...data.user,
        role: profileData?.role || role,
        business_id: profileData?.business_id,
        businessStaffRole: profileData?.business_staff_role
      });
      
      console.log('Login successful with role:', normalizedUser.role);
      
      return this.transformAuthResponse(data, normalizedUser);
    } catch (error) {
      return this.handleAuthError(error);
    }
  }

  /**
   * Register a new user with proper validation
   */
  async register(userData: { 
    email: string; 
    password: string; 
    name?: string; 
    role?: UserRole;
    business_id?: string;
    business_name?: string;
  }): Promise<AuthSession> {
    try {
      // Validate email
      if (!userData.email) {
        throw new Error('Email is required');
      }
      
      // Validate password
      const passwordValidation = validatePassword(userData.password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message);
      }
      
      const supabase = await createClient();
      
      // Register with Supabase
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            role: userData.role || 'business',
            name: userData.name,
            business_id: userData.business_id,
            business_name: userData.business_name
          }
        }
      });
      
      if (error) throw error;
      if (!data || !data.user) throw new Error('Registration failed');
      
      // Create profile record
      await supabase.from('profiles').upsert({
        id: data.user.id,
        role: userData.role || 'business',
        business_id: userData.business_id,
        full_name: userData.name,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      
      // Set up session refresh if we have a session
      if (data.session) {
        this.setupSessionRefresh();
      }
      
      // Get normalized user data
      const normalizedUser = await this.normalizeUserWithProfile(data.user);
      
      return this.transformAuthResponse(data, normalizedUser);
    } catch (error) {
      return this.handleAuthError(error);
    }
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    try {
      const supabase = await createClient();
      await supabase.auth.signOut();
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('Failed to log out');
    }
  }

  /**
   * Set up automatic session refresh
   */
  private setupSessionRefresh(): void {
    // Import dynamically to avoid circular dependencies
    import('./supabase').then(({ setupSessionRefresh }) => {
      setupSessionRefresh();
    });
  }

  /**
   * Get the current session
   */
  async getSession(): Promise<AuthSession | null> {
    try {
      const supabase = await createClient();
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) return null;
      
      // Get normalized user data
      const normalizedUser = await this.normalizeUserWithProfile(data.session.user);
      
      return this.transformAuthResponse({ 
        session: data.session,
        user: data.session.user
      }, normalizedUser);
    } catch (error) {
      console.error('Get session error:', error);
      return null;
    }
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<void> {
    try {
      const supabase = await createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      
      if (error) throw error;
    } catch (error) {
      return this.handleAuthError(error);
    }
  }

  /**
   * Update password
   */
  async updatePassword(password: string): Promise<void> {
    try {
      // Validate password
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message);
      }
      
      const supabase = await createClient();
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
    } catch (error) {
      return this.handleAuthError(error);
    }
  }
}

// Create a singleton instance
export const authServiceV2 = new AuthServiceV2();